/**
 * AccountCrmSection — relatiebeheer per account: accountmanager (dropdown),
 * contact-ritme (bellen / op locatie / lunch) met een verantwoordelijke per type,
 * loggen mét wie erbij was (aanwezigen), en een telling hoe vaak iemand iets deed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Chip, IconButton,
  CircularProgress, Tooltip, Divider, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { createTasksBatch as createMicrosoftTodoTasks, initMicrosoftTodoService } from '../../utils/microsoftTodoService';

const RITME_TYPES = [
  { key: 'bel', label: '📞 Bellen', doneLabel: 'Gebeld', respField: 'call_responsible' },
  { key: 'locatie', label: '🏢 Op locatie werken', doneLabel: 'Geweest', respField: 'visit_responsible' },
  { key: 'lunch', label: '☕ Lunch / koffie', doneLabel: 'Gedaan', respField: 'lunch_responsible' },
];
const FREQ_OPTIONS = [
  { value: '', label: 'Uit' },
  { value: 7, label: '1x per week' },
  { value: 14, label: '1x per 2 weken' },
  { value: 30, label: '1x per maand' },
  { value: 61, label: '1x per 2 maanden' },
  { value: 91, label: '1x per kwartaal (4x/jr)' },
  { value: 182, label: '2x per jaar' },
];
const FREQ_FIELD = { bel: 'call_every_days', locatie: 'visit_every_days', lunch: 'lunch_every_days' };

const fmtDate = (isoStr) => (isoStr ? new Date(isoStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : null);

export default function AccountCrmSection({ accountId, accountName, defaultAccountmanager }) {
  const { instance: msalInstance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todoInput, setTodoInput] = useState('');
  const [busy, setBusy] = useState(null);
  const [people, setPeople] = useState([]);          // namen voor de dropdowns
  const [persons, setPersons] = useState([]);        // volledige objecten {id,name,email} voor toewijzen
  const [logDlg, setLogDlg] = useState(null);        // { type, responsible, attendees }
  const [sendDlg, setSendDlg] = useState(null);      // { todo, assignee, destination }
  const [toast, setToast] = useState(null);          // { severity, text }
  const myEmail = (accounts[0]?.username || '').toLowerCase();

  useEffect(() => {
    if (msalInstance && accounts.length > 0) initMicrosoftTodoService(msalInstance, accounts[0]);
  }, [msalInstance, accounts]);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/crm`);
      if (r.ok) setData(await r.json());
    } catch { /* sectie is optioneel — pagina blijft werken */ }
    finally { setLoading(false); }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/planner-todo/persons`).then((r) => leesJson(r, 'Personen laden'))
      .then((d) => {
        const list = d.persons || [];
        setPersons(list);
        setPeople(list.map((p) => p.name || p.firstName).filter(Boolean).sort());
      })
      .catch((e) => setToast({ severity: 'error', text: e.message || 'Personen laden mislukt' }));
  }, []);

  const saveSettings = async (patch) => {
    setData((d) => ({ ...d, ...(patch.accountmanager !== undefined ? { accountmanager: patch.accountmanager } : {}) }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/crm/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (r.ok) setData(await r.json());
    } catch { /* volgende load herstelt */ }
  };

  const logActivity = async ({ type, title, responsible, attendees }) => {
    setBusy(type);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/crm/activities`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, title: title || null, responsible: responsible || null, attendees: attendees || [] }),
      });
      if (r.ok) setData(await r.json());
    } finally { setBusy(null); }
  };

  const openLog = (t) => {
    const r = data.ritme?.[t.key] || {};
    setLogDlg({ type: t.key, label: t.label, doneLabel: t.doneLabel, responsible: r.responsible || data.accountmanager || '', attendees: [] });
  };
  const confirmLog = async () => {
    const { type, responsible, attendees } = logDlg;
    setLogDlg(null);
    await logActivity({ type, responsible, attendees });
  };

  const addTodo = async () => {
    const title = todoInput.trim();
    if (!title) return;
    setTodoInput('');
    await logActivity({ type: 'todo', title });
  };

  const patchActivity = async (id, body) => {
    await authFetch(`${BACKEND_URL}/Api/klanten/crm/activities/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    load();
  };

  // Todo afvinken mét "ongedaan maken": optimistisch uit de lijst, dezelfde PATCH met
  // done:false zet 'm terug (toast + Ctrl+Z).
  const voltooiTodo = async (td) => {
    setData((d) => (d ? { ...d, todos: (d.todos || []).filter((t) => t.id !== td.id) } : d));
    await patchActivity(td.id, { done: true });
    biedOngedaanAan(`"${td.title}" afgerond`, () => patchActivity(td.id, { done: false }));
  };

  // #62 — Account-todo doorzetten: toewijzen aan iemand (of jezelf) en naar
  // persoonlijke Microsoft To-Do óf als Airtable-taak zetten.
  const notes = (todo) => [todo.title, accountName ? `Account: ${accountName}` : null].filter(Boolean).join('\n\n');
  const openSend = (todo) => setSendDlg({ todo, assignee: null, destination: 'todo' });
  const confirmSend = async () => {
    const { todo, assignee, destination } = sendDlg;
    setSendDlg(null);
    setBusy(`send:${todo.id}`);
    try {
      const isColleague = assignee && (assignee.email || '').toLowerCase() !== myEmail;
      if (destination === 'todo') {
        if (isColleague) {
          const r = await authFetch(`${BACKEND_URL}/Api/todo/create-for-user`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: assignee.email, tasks: [{ title: todo.title, body: notes(todo) }] }),
          });
          if (!r.ok) throw new Error('To-Do voor collega mislukt');
          setToast({ severity: 'success', text: `In de To-Do van ${assignee.name.split(' ')[0]} gezet ✔` });
        } else {
          const result = await createMicrosoftTodoTasks([{ name: todo.title, body: notes(todo) }]);
          if (!result.created?.length) throw new Error('To-Do aanmaken mislukt');
          setToast({ severity: 'success', text: 'Naar mijn Microsoft To-Do gezet ✔' });
        }
      } else {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks/create-batch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tasks: [{ name: todo.title, source: notes(todo), ...(assignee ? { assigneeId: assignee.id } : {}) }] }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) throw new Error(d.error || 'Airtable-taak aanmaken mislukt');
        setToast({ severity: 'success', text: `Als Airtable-taak aangemaakt${assignee ? ` voor ${assignee.name.split(' ')[0]}` : ''} ✔` });
      }
    } catch (e) {
      setToast({ severity: 'error', text: e.message || 'Doorzetten mislukt' });
    } finally { setBusy(null); }
  };

  if (loading) return <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}><CircularProgress size={20} /></Paper>;
  if (!data) return null;

  return (
    <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>🤝 Relatiebeheer</Typography>
        <Autocomplete freeSolo size="small" options={people} sx={{ minWidth: 240 }}
          value={data.accountmanager || defaultAccountmanager || ''}
          onChange={(_e, v) => saveSettings({ accountmanager: v || '' })}
          onInputChange={(_e, v, reason) => { if (reason === 'clear') saveSettings({ accountmanager: '' }); }}
          renderInput={(params) => <TextField {...params} label="Accountmanager"
            helperText={!data.accountmanager && defaultAccountmanager ? 'uit Airtable' : undefined} />} />
      </Stack>

      {/* Ritme per type: frequentie + verantwoordelijke + status + 'gedaan'-knop */}
      <Stack spacing={1}>
        {RITME_TYPES.map((t) => {
          const r = data.ritme?.[t.key] || {};
          return (
            <Stack key={t.key} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}
              sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #eef2f6' }}>
              <Typography sx={{ fontWeight: 700, width: 180, flexShrink: 0 }}>{t.label}</Typography>
              <TextField select size="small" value={r.enabled ? (r.every_days ?? '') : ''}
                onChange={(e) => saveSettings({ [FREQ_FIELD[t.key]]: e.target.value === '' ? null : Number(e.target.value) })}
                sx={{ width: 180 }}>
                {FREQ_OPTIONS.map((o) => <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              {/* Verantwoordelijke per type (default = accountmanager) */}
              <Autocomplete freeSolo size="small" options={people} sx={{ width: 170 }}
                value={r.responsible || ''}
                onChange={(_e, v) => saveSettings({ [t.respField]: v || null })}
                renderInput={(params) => <TextField {...params} label="Verantwoordelijk" placeholder="accountmgr" />} />
              {r.enabled ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 120 }}>
                    {r.last_done ? `Laatst: ${fmtDate(r.last_done)}` : 'Nog nooit gedaan'}
                    {r.next_due ? ` · vóór ${fmtDate(r.next_due)}` : ''}
                  </Typography>
                  <Chip size="small" label={r.overdue ? 'Actie nodig' : 'Op schema'}
                    color={r.overdue ? 'warning' : 'success'} sx={{ fontWeight: 700 }} />
                  <Button size="small" variant="outlined" disabled={busy === t.key}
                    onClick={() => openLog(t)}
                    startIcon={busy === t.key ? <CircularProgress size={13} /> : <CheckCircleOutlineIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ✓ {t.doneLabel}
                  </Button>
                </>
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>geen ritme ingesteld</Typography>
              )}
            </Stack>
          );
        })}
      </Stack>

      {/* Telling: hoe vaak deed/was iemand betrokken */}
      {(data.tally || []).length > 0 && (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Contactmomenten per persoon:</Typography>
          {data.tally.map((p) => (
            <Chip key={p.name} size="small" label={`${p.name.split(' ')[0]} · ${p.count}×`}
              sx={{ height: 20, fontSize: 11, bgcolor: '#eef2ff', color: '#4338ca' }} />
          ))}
        </Stack>
      )}

      {/* Todo's per account */}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography fontWeight={700}>Todo's voor dit account</Typography>
        <TextField size="small" placeholder="+ Todo… (Enter)" value={todoInput}
          onChange={(e) => setTodoInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTodo(); } }}
          sx={{ width: 280, '& .MuiInputBase-input': { py: 0.6, fontSize: 13 } }} />
      </Stack>
      <Stack spacing={0.5}>
        {(data.todos || []).map((td) => (
          <Stack key={td.id} direction="row" spacing={1} alignItems="center"
            sx={{ px: 1, py: 0.5, borderRadius: 1, border: '1px solid #eef2f6', '&:hover .crm-del': { opacity: 0.7 } }}>
            <Tooltip title="Afronden">
              <Box onClick={() => voltooiTodo(td)} sx={{
                width: 16, height: 16, borderRadius: '50%', border: '2px solid #10b981',
                cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: '#10b981' },
              }} />
            </Tooltip>
            <Typography variant="body2" sx={{ flex: 1 }}>{td.title}</Typography>
            <Tooltip title="Toewijzen · naar To-Do of Airtable">
              <IconButton className="crm-del" size="small" disabled={busy === `send:${td.id}`}
                onClick={() => openSend(td)}
                sx={{ opacity: 0, transition: 'opacity .15s' }}>
                {busy === `send:${td.id}` ? <CircularProgress size={14} /> : <SendOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton className="crm-del" size="small" onClick={() => patchActivity(td.id, { delete: true })}
              sx={{ opacity: 0, transition: 'opacity .15s' }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {(data.todos || []).length === 0 && (
          <Typography variant="caption" color="text.disabled">Geen open todo's.</Typography>
        )}
      </Stack>

      {/* Recente activiteiten (het meten) */}
      {(data.recent || []).length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography fontWeight={700} sx={{ mb: 0.5 }}>Recente activiteiten</Typography>
          <Stack spacing={0.25}>
            {data.recent.slice(0, 8).map((a) => (
              <Typography key={a.id} variant="caption" color="text.secondary">
                {fmtDate(a.done_at)} · {RITME_TYPES.find((t) => t.key === a.type)?.label || '✓ Todo'}
                {a.title ? ` — ${a.title}` : ''}
                {a.done_by ? ` · door ${a.done_by.split('@')[0]}` : ''}
                {(a.attendees || []).length ? ` · met ${a.attendees.map((x) => x.split(' ')[0]).join(', ')}` : ''}
              </Typography>
            ))}
          </Stack>
        </>
      )}

      {/* Log-dialoog: verantwoordelijke + aanwezigen vastleggen */}
      <Dialog open={!!logDlg} onClose={() => setLogDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{logDlg?.label} — vastleggen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete freeSolo size="small" options={people}
              value={logDlg?.responsible || ''}
              onChange={(_e, v) => setLogDlg((d) => ({ ...d, responsible: v || '' }))}
              renderInput={(params) => <TextField {...params} label="Verantwoordelijk" />} />
            <Autocomplete multiple freeSolo size="small" options={people}
              value={logDlg?.attendees || []}
              onChange={(_e, v) => setLogDlg((d) => ({ ...d, attendees: v }))}
              renderInput={(params) => <TextField {...params} label="Ook aanwezig (optioneel)" placeholder="collega's…" />} />
            <Typography variant="caption" color="text.secondary">
              Verantwoordelijke + aanwezigen tellen mee in "contactmomenten per persoon".
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDlg(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={confirmLog}
            sx={{ textTransform: 'none', fontWeight: 800 }}>✓ {logDlg?.doneLabel}</Button>
        </DialogActions>
      </Dialog>

      {/* Todo toewijzen + doorzetten naar To-Do of Airtable (#62) */}
      <Dialog open={!!sendDlg} onClose={() => setSendDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Todo toewijzen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{sendDlg?.todo?.title}</Typography>
            <Autocomplete size="small" options={persons}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={sendDlg?.assignee || null}
              onChange={(_e, v) => setSendDlg((d) => ({ ...d, assignee: v }))}
              renderInput={(params) => <TextField {...params} label="Aan wie? (leeg = mezelf)" placeholder="collega…" />} />
            <ToggleButtonGroup exclusive size="small" fullWidth
              value={sendDlg?.destination || 'todo'}
              onChange={(_e, v) => { if (v) setSendDlg((d) => ({ ...d, destination: v })); }}>
              <ToggleButton value="todo" sx={{ textTransform: 'none', fontWeight: 700 }}>Microsoft To-Do</ToggleButton>
              <ToggleButton value="airtable" sx={{ textTransform: 'none', fontWeight: 700 }}>Airtable-taak</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {sendDlg?.destination === 'airtable'
                ? 'Komt als taak in Airtable (met toegewezen persoon).'
                : sendDlg?.assignee && (sendDlg.assignee.email || '').toLowerCase() !== myEmail
                  ? `Komt in de persoonlijke To-Do van ${sendDlg.assignee.name.split(' ')[0]}.`
                  : 'Komt in jouw eigen Microsoft To-Do.'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDlg(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={confirmSend} sx={{ textTransform: 'none', fontWeight: 800 }}>Aanmaken</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>{toast.text}</Alert> : undefined}
      </Snackbar>
    </Paper>
  );
}
